import { Button, CircularProgress } from "@mui/material";
import { useState, useRef, useEffect } from "react";
import { apiClient } from "../utils/apiClient";
import {
  getUrlWithoutParams,
  useQueryParam,
  useRemoveAllUrlParams,
} from "../utils/url";
import MicrosoftIcon from "@mui/icons-material/Microsoft";
import { SSOLoginType } from "./UserLoginPage";
import { parseStateParameter, createStateParameter } from "./ssoUtils";
import {
  CredentialsLoginWithSSOResponse,
  CredentialsLoginWithSSOBody,
} from "@services/credentials/types";

export function generateCodeVerifier(length = 128): string {
  const charset =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
  const randomValues = new Uint8Array(length);
  crypto.getRandomValues(randomValues);
  return Array.from(randomValues)
    .map((n) => charset[n % charset.length])
    .join("");
}

export async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  const bytes = new Uint8Array(digest);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

export const MicrosoftLoginButton = ({
  onLoginStart,
  onSuccess,
  onFailure,
  code,
  redirectUrl,
  connectNewInbox,
}: {
  onLoginStart: (type: SSOLoginType) => void;
  onSuccess: (state: string) => void;
  onFailure: (reason: string) => void;
  code: string | null;
  redirectUrl: string | null;
  connectNewInbox: boolean;
}) => {
  const removeAllUrlParams = useRemoveAllUrlParams();
  const stateQueryUrl = useQueryParam("state")!;
  const [loading, setLoading] = useState(false);
  const requestedSSOWithCode = useRef(false);

  const handleLogin = async (codeResponse: string) => {
    setLoading(true);
    try {
      const response =
        await apiClient.post<CredentialsLoginWithSSOResponse>(
          "credentials/login_with_sso",
          {
            type: "microsoft",
            code: codeResponse,
            redirecturi: getUrlWithoutParams(),
            connectNewInbox,
            codeVerifier: sessionStorage.getItem("pkce_code_verifier") || "",
          } satisfies CredentialsLoginWithSSOBody
        );

      const { status, message } = response;
      if (status === "ok") {
        const redirectUrlFromState = parseStateParameter(stateQueryUrl);
        onSuccess(redirectUrlFromState || "");
      } else {
        onFailure(message || "Login failed");
      }
    } catch (error: any) {
      console.error("Login failed:", error.message || error);
      onFailure("An error occurred while attempting to log in. Try Again");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Handle Microsoft OAuth errors
    const searchParams = new URLSearchParams(window.location.search);
    const error = searchParams.get("error");
    if (error) {
      const errorDescription = searchParams.get("error_description") || error;
      onFailure(errorDescription);
      removeAllUrlParams();
    }

    // Handle authorization code
    if (code && !requestedSSOWithCode.current) {
      handleLogin(code).then(() => removeAllUrlParams());
      requestedSSOWithCode.current = true;
    }
  }, [code]);

  const initiateMicrosoftLogin = async () => {
    onLoginStart("microsoft");
    const clientId = process.env.REACT_APP_MICROSOFT_CLIENT_ID;
    const redirectUri = encodeURIComponent(getUrlWithoutParams());
    const scope = encodeURIComponent(
      "openid email profile offline_access User.Read Mail.ReadBasic Mail.ReadWrite Mail.Send"
    );
    const state = createStateParameter(redirectUrl, "microsoft");

    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    sessionStorage.setItem("pkce_code_verifier", codeVerifier);

    const authUrl =
      `https://login.microsoftonline.com/common/oauth2/v2.0/authorize` +
      `?client_id=${clientId}` +
      `&response_type=code` +
      `&redirect_uri=${redirectUri}` +
      `&scope=${scope}` +
      `&state=${state}` +
      `&prompt=select_account` +
      `&code_challenge=${codeChallenge}` +
      `&code_challenge_method=S256`;

    window.location.href = authUrl;
  };

  return (
    <Button
      variant="contained"
      color="primary"
      onClick={initiateMicrosoftLogin}
      disabled={loading}
      startIcon={<MicrosoftIcon />}
      fullWidth
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "10px",
        borderRadius: "5px",
        backgroundColor: "#2F2F2F",
        color: "#FFFFFF",
        cursor: "pointer",
        width: "100%",
        textTransform: "none",
      }}
    >
      {loading ? <CircularProgress size={24} /> : "Continue with Microsoft"}
    </Button>
  );
};
